import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaTrashAlt } from 'react-icons/fa';
import "./SingersPage.css";
import config from '../config/config';


const SingersPage = () => {
  const [singers, setSingers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSingerId, setActiveSingerId] = useState(null);
  const [expandedSingerId, setExpandedSingerId] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [newSinger, setNewSinger] = useState({ name: "", phoneNumbers: [] });
  const [editingSinger, setEditingSinger] = useState({ id: null, name: "", phoneNumbers: [] });
  const navigate = useNavigate();

  // Fetching the API URL from configuration
  const apiUrl = `${config.API_URL}/api`;

  useEffect(() => {
    const fetchSingers = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          console.error("No token found in localStorage");
          return;
        }

        const response = await axios.get(`${apiUrl}/singers`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000 // 10 second timeout
        });
        setSingers(response.data);
      } catch (error) {
        if (error.code === 'ECONNABORTED') {
          console.error("Request timeout - server not responding");
        } else if (error.response) {
          console.error(`Server error: ${error.response.status} - ${error.response.data?.error || 'Unknown error'}`);
        } else if (error.request) {
          console.error("No response from server - check if backend is running");
        }
      }
    };

    fetchSingers();
  }, [apiUrl]);

  const filteredSingers = singers.filter((singer) =>
    singer.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedSingers = [...filteredSingers].map(singer => ({
    ...singer,
    events: (singer.events || [])
      .filter(event => {
        const eventDate = new Date(event.date).setHours(0, 0, 0, 0);
        const today = new Date().setHours(0, 0, 0, 0);
        return eventDate >= today;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
  }));

  const toggleEvents = (id) => {
    if (activeSingerId === id) {
      setActiveSingerId(null);
      setExpandedSingerId(null);
    } else {
      setActiveSingerId(id);
    }
  };

  const handleEditSinger = (singer) => {
    setEditingSinger({
      id: singer.id,
      name: singer.name,
      phoneNumbers: singer.phoneNumbers || []
    });
    setIsEditPopupOpen(true);
  };

  const handleUpdateSinger = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found for edit operation");
        return;
      }

      const response = await axios.put(`${apiUrl}/singers/${editingSinger.id}`, {
        name: editingSinger.name,
        phoneNumbers: editingSinger.phoneNumbers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSingers(singers.map(s =>
        s.id === editingSinger.id
          ? { ...s, name: editingSinger.name, phoneNumbers: editingSinger.phoneNumbers }
          : s
      ));

      setIsEditPopupOpen(false);
      setEditingSinger({ id: null, name: "", phoneNumbers: [] });
    } catch (error) {
      console.error("Error updating singer:", error);
    }
  };

  const addPhoneNumber = (singerType) => {
    if (singerType === 'new') {
      setNewSinger({ ...newSinger, phoneNumbers: [...newSinger.phoneNumbers, ""] });
    } else {
      setEditingSinger({ ...editingSinger, phoneNumbers: [...editingSinger.phoneNumbers, ""] });
    }
  };

  const removePhoneNumber = (index, singerType) => {
    if (singerType === 'new') {
      const newPhoneNumbers = newSinger.phoneNumbers.filter((_, i) => i !== index);
      setNewSinger({ ...newSinger, phoneNumbers: newPhoneNumbers });
    } else {
      const newPhoneNumbers = editingSinger.phoneNumbers.filter((_, i) => i !== index);
      setEditingSinger({ ...editingSinger, phoneNumbers: newPhoneNumbers });
    }
  };

  const updatePhoneNumber = (index, value, singerType) => {
    if (singerType === 'new') {
      const newPhoneNumbers = [...newSinger.phoneNumbers];
      newPhoneNumbers[index] = value;
      setNewSinger({ ...newSinger, phoneNumbers: newPhoneNumbers });
    } else {
      const newPhoneNumbers = [...editingSinger.phoneNumbers];
      newPhoneNumbers[index] = value;
      setEditingSinger({ ...editingSinger, phoneNumbers: newPhoneNumbers });
    }
  };

  return (
    <div className="singers-container">
      {/* Back Button */}
      <button type="button" className="back-button" onClick={() => navigate('/')}>
        <FaArrowLeft className="back-icon" />
        Retour
      </button>

      {/* Page Header */}
      <div className="singers-page-header">
        <div className="header-content">
          <h1>Liste des Chanteurs</h1>
          <p className="singers-subtitle">
            Gérez tous vos chanteurs et leurs événements
          </p>
        </div>
        <div className="header-actions">
          <button className="add-button" onClick={() => setIsPopupOpen(true)}>
            Ajouter un chanteur
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <input
          type="text"
          placeholder="Rechercher un chanteur..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Singers List */}
      <div className="singers-list">
        {sortedSingers.length > 0 ? (
          sortedSingers.map((singer) => {
            const eventsToShow = expandedSingerId === singer.id
              ? singer.events
              : singer.events.slice(0, 3);

            return (
              <div key={singer.id} className="singer-item">
                <h3><strong>Name: </strong>{singer.name}</h3>
                <p><strong>Phone Numbers: </strong>
                  {singer.phoneNumbers && singer.phoneNumbers.length > 0
                    ? singer.phoneNumbers.join(", ")
                    : "No phone numbers"}
                </p>

                <button
                  onClick={() => toggleEvents(singer.id)}
                  className="events-toggle-button"
                >
                  {activeSingerId === singer.id ? "Hide Events" : "Show Events"}
                </button>

                {activeSingerId === singer.id && singer.events.length > 0 && (
                  <div className="events-dropdown">
                    <h4>Upcoming Events:</h4>
                    <ul>
                      {eventsToShow.map((event) => (
                        <li key={event.id}>
                          <strong>{event.name}</strong><br />
                          <strong>Date:</strong> {new Date(event.date).toLocaleDateString()}<br />
                          <strong>Location:</strong> {event.location}
                        </li>
                      ))}
                    </ul>

                    {singer.events.length > 3 && expandedSingerId !== singer.id && (
                      <button
                        className="see-all-button"
                        onClick={() => setExpandedSingerId(singer.id)}
                      >
                        See All
                      </button>
                    )}
                  </div>
                )}

                {activeSingerId === singer.id && singer.events.length === 0 && (
                  <p>No upcoming events</p>
                )}

                <div className="singer-actions">
                  <button
                    onClick={() => handleEditSinger(singer)}
                    className="edit-button"
                    title="Edit Singer"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>

                  <button
                    onClick={() => {
                      const confirmDelete = window.confirm("Do you really want to delete this singer?");
                      if (confirmDelete) {
                        const token = localStorage.getItem("token");
                        if (!token) {
                          console.error("No token found for delete operation");
                          return;
                        }
                        axios.delete(`${apiUrl}/singers/${singer.id}`, {
                          headers: { Authorization: `Bearer ${token}` }
                        })
                          .then(() => setSingers(singers.filter(s => s.id !== singer.id)))
                          .catch(error => console.error("Error deleting singer:", error));
                      }
                    }}
                    className="delete-button"
                    title="Delete Singer"
                  >
                    <FaTrashAlt className="trash-icon" />
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <p>Aucun chanteur trouvé.</p>
        )}
      </div>

      {isPopupOpen && (
        <div className="modal-overlay">
          <div className="luxury-modal">
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <div className="title-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="8.5" cy="7" r="4"></circle>
                    <line x1="20" y1="8" x2="20" y2="14"></line>
                    <line x1="17" y1="11" x2="23" y2="11"></line>
                  </svg>
                </div>
                <h3>Ajouter un Chanteur</h3>
              </div>
              <button className="modal-close" onClick={() => setIsPopupOpen(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const token = localStorage.getItem("token");
              if (!token) {
                console.error("No token found for add operation");
                return;
              }
              axios.post(`${apiUrl}/singers`, newSinger, {
                headers: { Authorization: `Bearer ${token}` }
              })
                .then(response => {
                  setSingers([...singers, response.data]);
                  setIsPopupOpen(false);
                  setNewSinger({ name: "", phoneNumbers: [] });
                })
                .catch(error => console.error("Error adding singer:", error));
            }} className="luxury-form">
              <div className="form-group luxury-input">
                <label>Nom complet *</label>
                <div className="input-with-icon">
                  <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <input
                    type="text"
                    value={newSinger.name}
                    onChange={(e) => setNewSinger({ ...newSinger, name: e.target.value })}
                    placeholder="Entrez le nom du chanteur"
                    required
                  />
                </div>
              </div>

              <div className="form-group luxury-input">
                <label>Numéros de Téléphone</label>
                <div className="phone-numbers-container">
                  {newSinger.phoneNumbers.map((phone, index) => (
                    <div key={index} className="phone-number-row-luxury">
                      <div className="input-with-icon">
                        <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l2.28-2.28a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                        </svg>
                        <input
                          type="text"
                          value={phone}
                          onChange={(e) => updatePhoneNumber(index, e.target.value, 'new')}
                          placeholder={`Numéro ${index + 1}`}
                        />
                        <button
                          type="button"
                          onClick={() => removePhoneNumber(index, 'new')}
                          className="remove-phone-btn-luxury"
                          title="Supprimer"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addPhoneNumber('new')}
                    className="btn-add-phone-luxury"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Ajouter un numéro
                  </button>
                </div>
              </div>

              <div className="modal-actions-luxury">
                <button type="button" onClick={() => setIsPopupOpen(false)} className="btn-luxury btn-luxury-secondary">Annuler</button>
                <button type="submit" className="btn-luxury btn-luxury-primary">Ajouter</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isEditPopupOpen && (
        <div className="modal-overlay">
          <div className="luxury-modal">
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <div className="title-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </div>
                <h3>Modifier le Chanteur</h3>
              </div>
              <button className="modal-close" onClick={() => setIsEditPopupOpen(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <form onSubmit={handleUpdateSinger} className="luxury-form">
              <div className="form-group luxury-input">
                <label>Nom complet *</label>
                <div className="input-with-icon">
                  <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <input
                    type="text"
                    value={editingSinger.name}
                    onChange={(e) => setEditingSinger({ ...editingSinger, name: e.target.value })}
                    placeholder="Entrez le nom du chanteur"
                    required
                  />
                </div>
              </div>

              <div className="form-group luxury-input">
                <label>Numéros de Téléphone</label>
                <div className="phone-numbers-container">
                  {editingSinger.phoneNumbers.map((phone, index) => (
                    <div key={index} className="phone-number-row-luxury">
                      <div className="input-with-icon">
                        <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l2.28-2.28a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                        </svg>
                        <input
                          type="text"
                          value={phone}
                          onChange={(e) => updatePhoneNumber(index, e.target.value, 'edit')}
                          placeholder={`Numéro ${index + 1}`}
                        />
                        <button
                          type="button"
                          onClick={() => removePhoneNumber(index, 'edit')}
                          className="remove-phone-btn-luxury"
                          title="Supprimer"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addPhoneNumber('edit')}
                    className="btn-add-phone-luxury"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Ajouter un numéro
                  </button>
                </div>
              </div>

              <div className="modal-actions-luxury">
                <button type="button" onClick={() => setIsEditPopupOpen(false)} className="btn-luxury btn-luxury-secondary">Annuler</button>
                <button type="submit" className="btn-luxury btn-luxury-primary">Modifier</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SingersPage;